import React, {Fragment, useContext, useEffect, useState} from "react";
import axiosConfig from "../../../../../Config/axios";
import {getApplicationRoute} from "../../../../../Route/RouteIndex";
import DataTable from "react-data-table-component";
import {TableColumn} from "react-data-table-component";
import Loader from "../../../../../Layout/Loader";
import IconBtn from "../../../../../CommonElements/IconButton";
import AuthorizationContext from "../../../../../_helper/Authorization";
import type {AuthorizationContextType} from "../../../../../_helper/Authorization/AuthorizationTypes";
import type {Wallet} from "../../Types/WalletTypes";
import WalletContext from "../../../Wallet/Context";
import type {WalletContextType} from "../../../Wallet/Context/WalletContext.types";

interface walletsTableProps {
    params: any,
    tableOptions?: any,
}

const WalletsTable = React.forwardRef(({
                                           params,
                                           tableOptions = {delete: true, view: true}
                                       }: walletsTableProps, ref) => {
    const [loaderVisibility, setLoaderVisibility] = useState(false);
    const [wallets, setWallets] = useState<Array<Wallet>>([])
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [perPage, setPerPage] = useState<number>(10);
    const [totalRows, setTotalRows] = useState<number>(0);
    const authContext = useContext(AuthorizationContext) as AuthorizationContextType;
    const {errorHandler} = authContext || {};
    const walletContext = useContext(WalletContext) as WalletContextType;
    const {getFormattedAmount, getOwerTypeColor} = walletContext || {};

    React.useImperativeHandle(ref, () => ({
        getWallets,
        addWallet
    }));

    const getWallets = (page: number = currentPage, limit: number = perPage) => {
        setLoaderVisibility(true);
        const config = {params: {...params, page, per_page: limit}};
        axiosConfig
            .get(getApplicationRoute('wallet'), config)
            .then((response: any) => {
                if (response?.data?.data?.data) {
                    setWallets(response.data.data.data);
                }
                if (response?.data?.data?.meta) {
                    setCurrentPage(response.data.data.meta.current_page);
                    setPerPage(response.data.data.meta.per_page);
                    setTotalRows(response.data.data.meta.total);
                }
                setLoaderVisibility(false);
            })
            .catch((exception: any) => {
                errorHandler(exception);
                setLoaderVisibility(false);
            });
    }

    useEffect(() => {
        getWallets(1, perPage);
    }, [params])

    const deleteWallet = (id: any) => {
        setLoaderVisibility(true);
        axiosConfig
            .delete(`${getApplicationRoute('wallet')}/${id}`, {})
            .then((response: { data: { data: { id: any; }; }; }) => {
                if (response?.data?.data) {
                    setWallets(current => current.filter(user => {
                        return user.id !== response?.data?.data?.id;
                    }));
                }
                setLoaderVisibility(false);
            })
            .catch((exception: any) => {
                errorHandler(exception);
                setLoaderVisibility(false);
            });
    }

    const addWallet = (wallet: { id: any; }) => {
        const walletList = [...wallets];
        const existingWalletIndex = walletList.findIndex(existingWallet => {
            return existingWallet.id === wallet.id;
        });
        if (existingWalletIndex !== -1) {
            walletList[existingWalletIndex] = wallet;
        } else {
            walletList.push(wallet);
        }
        setWallets(walletList);
    }

    const resolveOwnerName = (wallet: Wallet) => {
        return wallet?.owner?.name || wallet?.owner?.user?.name || wallet?.owner?.full_name || wallet?.owner?.title || '';
    }

    const resolveOwnerType = (wallet: Wallet) => {
        return wallet?.owner_type || 'unknown';
    }

    const resolveWalletType = (wallet: Wallet) => {
        return wallet?.wallet_type || '';
    }

    const getBadgeColor = (walletType: string): string => {
        const type = walletType?.toLowerCase() || '';
        if (type.includes('cash')) return 'badge-light-success';
        if (type.includes('point')) return 'badge-light-primary';
        return 'badge-light-secondary';
    };

    const walletTableColumns = [
        {
            name: 'Owner',
            selector: (wallet: Wallet) => resolveOwnerName(wallet),
            sortable: false,
            cell: (wallet: Wallet) => {
                const ownerType = resolveOwnerType(wallet);
                return (
                    <div className='d-flex align-items-center gap-1'>
                        <span className={`badge badge-light-${getOwerTypeColor(ownerType)} m-r-20`}>
                            {ownerType}
                        </span>
                        <span>{resolveOwnerName(wallet) || '-'}</span>
                    </div>
                );
            },
        } as TableColumn<Wallet>,
        {
            name: 'Wallet Details',
            cell: (wallet: Wallet) => {
                const walletType = resolveWalletType(wallet);
                const balance = wallet?.current_balance ?? 0;
                const badgeColor = getBadgeColor(walletType);
                const walletTypeDisplay = walletType || 'Unknown';

                return (
                    <div className='d-flex align-items-center gap-1' title={`${walletTypeDisplay} Balance`}>
                        <span className={`badge ${badgeColor} m-r-20`}>
                            {walletTypeDisplay}
                        </span>
                        <span>{getFormattedAmount(balance, walletType.toLowerCase().includes('cash') ? 'wallet-type-cash' : 'wallet-type-points', 1)}</span>
                    </div>
                );
            },
            sortable: true,
            center: true,
        } as TableColumn<Wallet>,
        {
            name: 'Option',
            cell: (wallet: Wallet) => (
                <div className='d-flex align-items-right gap-2'>
                    {tableOptions?.view &&
                        <IconBtn link={`${getApplicationRoute('wallet')}/${wallet?.id}`} type={'view'}
                                 permission={'read-wallet'}/>}
                    {tableOptions?.delete &&
                        <IconBtn confirm={{confirm:true}} onclick={() => deleteWallet(wallet?.id)} type={'delete'}
                                 permission={'delete-wallet'}/>}
                </div>
            ),
            sortable: false,
            right: true,
        } as TableColumn<Wallet>
    ];
    return <Fragment>
        <Loader visibility={loaderVisibility}/>
        <DataTable pagination paginationServer columns={walletTableColumns as any}
                   data={wallets}
                   paginationTotalRows={totalRows}
                   paginationPerPage={perPage}
                   paginationDefaultPage={currentPage}
                   onChangePage={(page) => getWallets(page, perPage)}
                   onChangeRowsPerPage={(newPerPage, page) => {
                       setPerPage(newPerPage);
                       getWallets(page, newPerPage);
                   }}
                   highlightOnHover={true}
                   striped={true} responsive={true}/>
    </Fragment>
});
export default WalletsTable;
